import { Button } from '@calizahq/react-components-v2'
import { WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { Agent } from '@sales-app/types/Agent'
import { BookablePersonDuplicate } from '@sales-app/types/BookablePersonDuplicate'
import { BookablePersonDuplicateDecision } from '@sales-app/types/BookablePersonDuplicateDecision'
import { BookablePersonType } from '@sales-app/types/BookablePersonType'
import { DisplayCity } from '@sales-app/types/DisplayCity'
import { PersonsReservationRow } from '@sales-app/types/Person'
import { Status } from '@sales-app/types/PotentialBooking'
import { formatInTimeZone } from 'date-fns-tz'
import { useNavigate } from 'react-router-dom'

export const PotentialBookingColumns = [
  {
    Header: 'Display City',
    accessor: 'displayCity.name',
    width: 100,
    Cell: function Cell({ value }: any) {
      return (
        <div className="flex items-center justify-center">
          <div className="flex flex-col font-medium">
            <h3 className="font-bold">{value}</h3>
          </div>
        </div>
      )
    },
  },
  {
    Header: 'Status',
    accessor: 'potentialBookingStatus',
    width: 100,
    Cell: ({ value }: { value: string }) => {
      const className = getPotentialBookingStatusClassNames(value)
      return (
        <div className="flex w-full items-center justify-center">
          <div
            className={`w-fit-content cursor-pointer rounded-lg px-3 py-2 capitalize leading-140
               ${className}`}
          >
            {value}
          </div>
        </div>
      )
    },
  },
  {
    Header: 'Created',
    accessor: 'createdAt',
    width: 100,
    Cell: function Cell({ value = '' }: { value: string }) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM dd, yyyy hh:mm aa')
        return time
      } catch (e) {
        return 'Invalid  Date'
      }
    },
  },
  {
    Header: 'Source',
    accessor: 'potentialBookingSource',
    width: 100,
    Cell: ({ value }: { value: string }) => {
      return (
        <div className="flex w-full items-center justify-center">
          <div className={`w-fit-content cursor-pointer rounded-lg px-3 py-2 capitalize leading-140`}>{value}</div>
        </div>
      )
    },
  },
  {
    Header: 'Agent',
    accessor: 'agent',
    width: 100,
    Cell: ({ value }: { value: Agent | null }) => {
      return (
        <div className="flex w-full items-center justify-center">
          <div className={`w-fit-content cursor-pointer rounded-lg px-3 py-2 capitalize leading-140`}>
            {value?.name}
          </div>
        </div>
      )
    },
  },
  {
    accessor: 'id',
    width: 60,
    maxWidth: 60,
    canResize: false,
    Cell: function Cell({ row }: { row: { original: PersonPotentialBookingRow } }) {
      const navigate = useNavigate()
      const openPotentialBooking = () => {
        if (row.original.bookablePersonId) {
          const path = getWorkspacePath({
            view: WorkspaceView.POTENTIALBOOKING,
            type: row.original.bookablePersonType,
            id: row.original.bookablePersonId,
            potentialBookingId: row.original.id,
          })

          navigate(path)
        }
      }
      return (
        <div className="flex items-center">
          <Button className=" bg-text-semantic-success" onClick={openPotentialBooking} size="small">
            View
          </Button>
        </div>
      )
    },
  },
]

export const ReservationColumns = [
  {
    Header: 'Name',
    width: 100,
    Cell: function Cell({ row }: { row: { original: PersonsReservationRow } }) {
      return (
        <div className="flex items-center justify-center">
          <div className="flex flex-col font-medium">
            <h3 className="font-bold">{row?.original.home.searchDocument}</h3>
          </div>
        </div>
      )
    },
  },
  {
    Header: 'Move In',
    width: 100,
    accessor: 'moveInDate',
    Cell: function Cell({ value = '' }: { value: string }) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM dd, yyyy')
        return time
      } catch (e) {
        return 'Invalid  Date'
      }
    },
  },
  {
    Header: 'Move Out',
    width: 100,
    accessor: 'moveOutDate',
    Cell: function Cell({ value = '' }: { value: string }) {
      if (!value) return '---'

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const time = formatInTimeZone(value, timezone, 'MMM dd, yyyy')
        return time
      } catch (e) {
        return 'Invalid  Date'
      }
    },
  },
]

export const DuplicateColumns = (handleDuplicateMerge: (duplicateReview: BookablePersonDuplicate) => void) => {
  return [
    {
      Header: 'Name',
      width: 100,
      accessor: 'originalName',
      Cell: function Cell({ row }: { row: { original: BookablePersonDuplicate } }) {
        const navigate = useNavigate()
        const { originalBookablePersonId, originalName, originalBookablePersonType } = row.original

        const openWorkspace = () => {
          const path = getWorkspacePath({
            view: WorkspaceView.SUMMARY,
            type: originalBookablePersonType,
            id: originalBookablePersonId,
            potentialBookingId: null,
          })

          navigate(path)
        }

        return (
          <span className="cursor-pointer underline" onClick={openWorkspace} role="presentation">
            {originalName}
          </span>
        )
      },
    },
    {
      Header: 'Email',

      accessor: 'originalEmail',
    },
    {
      Header: 'Phone',
      width: 60,
      accessor: 'originalPhoneNumber',
    },
    {
      accessor: 'id',
      width: 50,
      Cell: function Cell({ row }: { row: { original: BookablePersonDuplicate } }) {
        if (row.original.decision === BookablePersonDuplicateDecision.Review) {
          return (
            <button
              className={`flex h-10 w-fit-content items-center justify-center rounded-md bg-action-primary px-4 py-2 text-white`}
              onClick={() => handleDuplicateMerge(row.original)}
            >
              {row.original.decision}
            </button>
          )
        } else {
          return row.original.decision
        }
      },
    },
  ]
}

export interface PersonPotentialBookingRow {
  agent: Agent | null
  bookablePersonId: number
  bookablePersonType: BookablePersonType
  createdAt: string
  displayCity: DisplayCity | null
  id: number
  potentialBookingSource: string
  potentialBookingStatus: string
}

export const getPotentialBookingStatusClassNames = (status: string) => {
  switch (status) {
    case Status.APPLICATION_STARTED: {
      return 'bg-pink-100 text-black'
    }
    case Status.NEW: {
      return 'bg-red-100 text-black'
    }
    case Status.NEEDS_ACTION:
    case Status.WORKING: {
      return 'bg-orange-100 text-black'
    }
    case Status.FOLLOW_UP: {
      return 'bg-yellow-100 text-black'
    }
    case Status.REENGAGED: {
      return 'bg-blue-100 text-black'
    }
    case 'Replied to automation':
      return 'bg-yellow-200 text-action-input'
    case 'On hold':
    case Status.AWAITING_RESPONSE:
      return 'action-input text-white'
    case Status.PENDING:
      return 'bg-yellow-200 text-action-input'
    case Status.BOOKED:
    case 'Approved':
      return 'action-primary text-white'
    case 'Denied':
      return 'bg-red-300 text-white'
    default:
      return 'bg-border-secondary text-action-input'
  }
}
